// ABOUTME: Family-wide "Ready to celebrate" card — top 3 most-recently-completed goals across all children
// ABOUTME: "Celebrate" fires goalsAPI.celebrateGoal directly, no confirmation modal, unlike the per-child flow

import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, Chip } from '@mui/material';
import { goalsAPI } from '../../services/api';
import { useSignalR } from '../../contexts/SignalRContext';
import { EnhancedChildDashboard, Goal } from '../../types/api';
import { BilingualLabel } from '../BilingualLabel';
import { FamilyStripQueueModal } from './FamilyStripQueueModal';

export interface CelebrationQueueCardProps {
  familyId: number;
  familyChildren: EnhancedChildDashboard[];
}

function CelebrationRow({ goal, onCelebrated }: { goal: Goal; onCelebrated: () => void | Promise<void> }) {
  const [isCelebrating, setIsCelebrating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCelebrate = async () => {
    setIsCelebrating(true);
    setError(null);
    try {
      await goalsAPI.celebrateGoal(goal.id);
      await onCelebrated();
    } catch (err) {
      console.error(`Failed to celebrate goal ${goal.id}`, err);
      setError('Failed to celebrate — please try again.');
    } finally {
      setIsCelebrating(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', border: '1px solid', borderColor: 'secondary.dark', borderRadius: '12px', p: 1.5 }} data-testid={`celebration-row-${goal.id}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{goal.childName} · {goal.title}</Typography>
          {goal.rewardDescription && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>{goal.rewardDescription}</Typography>
          )}
        </Box>
        <Button
          size="small"
          variant="contained"
          onClick={handleCelebrate}
          disabled={isCelebrating}
          sx={{ bgcolor: 'secondary.main', color: 'background.paper', borderRadius: '999px' }}
          data-testid={`celebration-celebrate-${goal.id}`}
        >
          <BilingualLabel term="celebrate" english="Celebrate" />
        </Button>
      </Box>
      {error && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }} data-testid={`celebration-error-${goal.id}`}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

export const CelebrationQueueCard: React.FC<CelebrationQueueCardProps> = ({ familyId, familyChildren }) => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const { on } = useSignalR();

  const load = async () => {
    const response = await goalsAPI.getFamilyGoalsPaginated(familyId, {
      filterStatus: 'completed',
      page: 1,
      pageSize: 200,
    });
    const sorted = [...response.data].sort(
      (a, b) => new Date(b.completedAt ?? 0).getTime() - new Date(a.completedAt ?? 0).getTime()
    );
    setGoals(sorted);
  };

  useEffect(() => {
    load();
    const unsubUpdated = on('GoalUpdated', () => load());
    const unsubReached = on('GoalReached', () => load());
    return () => {
      unsubUpdated();
      unsubReached();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, on]);

  const top3 = goals.slice(0, 3);
  const hasAnyGoalsConfigured = familyChildren.some(c => c.activeGoals.length > 0);

  return (
    <Paper sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'secondary.dark', borderRadius: '18px', p: 2.5 }} elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          <BilingualLabel term="celebration" english="Ready to celebrate" />
        </Typography>
        <Chip label={goals.length} size="small" sx={{ bgcolor: 'secondary.main', color: 'background.paper', fontWeight: 700 }} />
      </Box>
      {goals.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="celebration-empty-state">
          {hasAnyGoalsConfigured
            ? 'All caught up — no goals finished yet, keep going!'
            : 'Nothing set up yet — set a goal to get started.'}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {top3.map(g => <CelebrationRow key={g.id} goal={g} onCelebrated={load} />)}
        </Box>
      )}
      {goals.length > 3 && (
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Button size="small" onClick={() => setModalOpen(true)} sx={{ color: 'secondary.main', fontWeight: 700 }}>
            ✦ {goals.length - 3} more waiting — plan a celebration night →
          </Button>
        </Box>
      )}
      <FamilyStripQueueModal
        open={modalOpen}
        title="Ready to celebrate"
        onClose={() => setModalOpen(false)}
        rows={goals.map(g => ({ key: String(g.id), node: <CelebrationRow goal={g} onCelebrated={load} /> }))}
      />
    </Paper>
  );
};
